import { getAppVersion } from 'utils';
import { request } from 'modules/shell/utils/request';

// Log levels are defined according to server definition:
// https://stash.mynmg.com/projects/OMNI/repos/osp-logs/browse/src/models/logMessage.schema.json
const LOG_LEVELS = {
  off: -1,
  error: 0,
  warn: 1,
  info: 2,
  debug: 4,
};

// ------------------- Core ----------------------
class LogContext {
  constructor() {
    // Define context interface
    this.context = {
      source: 'osp-ui', // field as an source identified for OSP UI on logs server
      version: getAppVersion(), // app version in current environment - semiver + git rev (e.g. "0.1.0-e8fb398")
      associatePin: 0,
      activeADGroup: '',
      stacktrace: null, // Actual for "error" level only,
    };
  }

  update(contextData) {
    this.context = Object.keys(this.context).reduce((res, contextKey) => {
      if (contextData[contextKey]) {
        res[contextKey] = contextData[contextKey];
      } else {
        res[contextKey] = this.context[contextKey];
      }

      return res;
    }, {});
  }

  generateLogPayload(message, extraContext = {}) {
    const logPayload = { ...extraContext, ...this.context };

    if (message instanceof Error) {
      logPayload.message = message.message;
      logPayload.stacktrace = message.stack;
    } else {
      logPayload.message = message;
    }

    return logPayload;
  }
}

// ------------------- Transports ----------------------

// Print log to `window.console`
function consoleTransport({ message, context, level, extraContext }) {
  console[level](context.generateLogPayload(message, extraContext)); // eslint-disable-line
}

// Send log to log server
function remoteTransport({ message, context, level, extraContext, config }) {
  request(config.url, {
    method: 'post',
    body: {
      logLevel: level,
      logData: context.generateLogPayload(message, extraContext),
    },
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': config.apiKey,
    },
  });
}

const LOGGER_TRANSPORTS_MAP = {
  console: consoleTransport,
  remote: remoteTransport,
};

// ------------------- Logger --------------------------
/**
 * Create instance of app logger with next interface
 *
 * Logger {
 *    - setLogLevel(level: string): Logger
 *    - updateContext(contextData: Object): Logger
 *    - log(level: String, message: String): Logger
 *    - ...shortcut log methods - listed in `LOG_LEVELS`
 *}
 *
 * @param {Object} config
 * @returns {{}|{log: log, setLogLevel: setLogLevel, updateContext: (function(*=): void)}}
 */
export const createLogger = (config) => {
  const logContext = new LogContext();
  let logLevel = config.level || LOG_LEVELS.off;

  // prepare logger transports
  const loggerTransports = Object.keys(config.transports).reduce((res, transportName) => {
    const { enabled, ...restConfig } = config.transports[transportName];

    if (enabled) {
      res.push((payload) => LOGGER_TRANSPORTS_MAP[transportName]({ ...payload, config: restConfig }));
    }

    return res;
  }, []);

  // methods
  const updateContext = (data) => {
    logContext.update(data);

    return this;
  };

  const setLogLevel = (level) => {
    if (!LOG_LEVELS[level]) {
      throw new Error(`logger.setLevel: unrecognized log level: ${level}`);
    }

    logLevel = level;

    return this;
  };

  /**
   * Common log method
   *
   * @param level
   * @param message
   * @param extraContext - additional context data (e.g. body of POST request). NOTE: this data cannot override LogContext data
   *
   * @returns {this}
   */
  const log = (level, message, extraContext = {}) => {
    if (typeof LOG_LEVELS[level] !== 'number') {
      throw new Error(`logger.log: unrecognized log level: ${level}`);
    }

    if (LOG_LEVELS[level] > LOG_LEVELS[logLevel]) {
      return this;
    }

    // send log payload to all enabled logger transports
    loggerTransports.forEach((transport) => transport({ message, level, context: logContext, extraContext }));

    return this;
  };

  // generate shortcut log methods
  const logMethods = Object.keys(LOG_LEVELS).reduce(
    (res, level) => ({ ...res, [level]: (message, extraContext) => log(level, message, extraContext) }),
    {},
  );

  return {
    ...logMethods,
    updateContext,
    setLogLevel,
    log,
  };
};

export default createLogger;
